const TZ = "Africa/Kampala";

export type EventShareOptions = {
  dateIso?: string;
  eventTitle?: string | null;
  url: string;
};

export function getDefaultEventShareUrl(): string {
  if (typeof window === "undefined") return "";
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}`;
}

export function formatEventShareMessage({
  dateIso,
  eventTitle,
  url,
}: EventShareOptions): string {
  return `${formatEventShareText({ dateIso, eventTitle })} ${url}`;
}

export function formatEventShareText({
  dateIso,
  eventTitle,
}: Pick<EventShareOptions, "dateIso" | "eventTitle">): string {
  const brand = "Together In Heaven";
  const title = eventTitle?.trim() || "worship overnight";

  if (dateIso) {
    const when = new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: TZ,
    }).format(new Date(dateIso));
    return `I'm planning to attend ${brand} ${title} on ${when}. Join me:`;
  }

  return `Join me at ${brand} ${title}. RSVP & details:`;
}

export function whatsAppShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function facebookShareUrl(url: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

export function xShareUrl(text: string, url: string): string {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
}

export function linkedInShareUrl(url: string): string {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
}

export function emailShareUrl(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function canNativeShare(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

export async function nativeShareEvent(opts: EventShareOptions): Promise<boolean> {
  if (!canNativeShare()) return false;
  const text = formatEventShareMessage(opts);
  try {
    await navigator.share({
      title: "Together In Heaven",
      text,
      url: opts.url,
    });
    return true;
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return false;
    return false;
  }
}
